/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
  SLRProtectedRoute,
  SLRSpinner,
  useAuth
} from '@slr/shared-library';
import {
  ADMIN_ONBOARDING_REQUEST_PATH,
  ADMIN_ONBOARDING_REQUESTS_PATH,
  ADMINISTRATION_PATH,
  CHOOSE_ORGANIZATION_PATH,
  COMPLETE_REGISTRATION_PATH,
  DELETED_PROFILE_PATH,
  INVITATION_CHECK,
  INVITATION_HANDLE,
  legalRouts,
  ONBOARDING_PATH,
  PROFILE_DATA_PRIVACY_PATH,
  PROFILE_EMAIL_UPDATE_PATH,
  PROFILE_ORGANIZATION_DETAILS_PATH,
  PROFILE_ORGANIZATIONS_PATH,
  PROFILE_PASSWORD_UPDATE_PATH,
  PROFILE_PATH,
  PROFILE_SECURITY_PATH
} from 'configs';
import { useIsPlatformAdmin } from 'hooks/admin/useIsPlatformAdmin';
import { useGetCurrentUserProfileWithSolution1 } from 'hooks';
import { FC, useCallback } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import {
  AdministrationDashboardPage,
  AdministrationOffersPage,
  AdministrationOrganizationsPage,
  AdministrationPage,
  AdministrationUsersPage
} from './administration';
import { ChooseOrganizationPage } from './choose-organization'; import { InvitationCheckPage, InvitationHandlePage } from './invitations';
import { LegalRoutingWrapper } from './legal/LegalRoutingWrapper';
import { DraftPage } from './onboarding/draft/DraftPage';
import { RequestPage } from './onboarding/request/RequestPage';
import { RequestsPage } from './onboarding/request/RequestsPage';
import { OrganizationDetails, OrganizationRedirect } from './organizations';
import {
  DeletedProfile,
  ProfileDataPrivacy,
  ProfileDetails,
  ProfileEmailChange,
  ProfilePage,
  ProfilePasswordChange,
  ProfileSecurity
} from './profile';
import { CompleteRegistrationPage } from './profile/CompleteRegistrationPage';

export const UserPortalRouting: FC = () => {
  const { isAuthenticated, login, hasClientRole } = useAuth();
  const isPlatformAdmin = useIsPlatformAdmin();

  const handleAuthentication = useCallback(() => {
    return login(window.location.href);
  }, [login]);

  const isOnboardingAdmin = hasClientRole('onboarding', 'mdl');

  return (
    <Routes>
      <Route
        element={
          <SLRProtectedRoute
            condition={isAuthenticated}
            callback={handleAuthentication}
            callbackSpinner={<SLRSpinner />}
          />
        }
      >
        <Route index element={<Navigate to={PROFILE_PATH} />} />
        <Route
          path={PROFILE_PATH}
          element={
            <ProfilePage>
              <ProfileDetails />
            </ProfilePage>
          }
        />

        <Route
          path={PROFILE_EMAIL_UPDATE_PATH}
          element={
            <ProfilePage>
              <ProfileEmailChange />
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_PASSWORD_UPDATE_PATH}
          element={
            <ProfilePage>
              <ProfilePasswordChange />
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_SECURITY_PATH}
          element={
            <ProfilePage>
              <ProfileSecurity />
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_DATA_PRIVACY_PATH}
          element={
            <ProfilePage>
              <ProfileDataPrivacy />
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_ORGANIZATIONS_PATH}
          element={
            <ProfilePage>
              <BusinessAccountRoute>
                <OrganizationRedirect />
              </BusinessAccountRoute>
            </ProfilePage>
          }
        />
        <Route
          path={PROFILE_ORGANIZATION_DETAILS_PATH}
          element={
            <ProfilePage>
              <BusinessAccountRoute>
                <OrganizationDetails />
              </BusinessAccountRoute>
            </ProfilePage>
          }
        />

        <Route
          path={CHOOSE_ORGANIZATION_PATH}
          element={<ChooseOrganizationPage />}
        />
        <Route path={INVITATION_HANDLE} element={<InvitationHandlePage />} />

        <Route element={<SLRProtectedRoute condition={isOnboardingAdmin} />}>
          <Route
            path={ADMIN_ONBOARDING_REQUESTS_PATH}
            element={<RequestsPage />}
          />
          <Route
            path={ADMIN_ONBOARDING_REQUEST_PATH}
            element={<RequestPage />}
          />
        </Route>

        <Route element={<SLRProtectedRoute condition={isPlatformAdmin} />}>
          <Route path={ADMINISTRATION_PATH} element={<AdministrationPage />}>
            <Route
              index
              element={<Navigate to="users" replace />}
            />
            <Route path="users" element={<AdministrationUsersPage />} />
            <Route
              path="organizations"
              element={<AdministrationOrganizationsPage />}
            />
            <Route path="offers" element={<AdministrationOffersPage />} />
            <Route
              path="dashboard"
              element={<AdministrationDashboardPage />}
            />
          </Route>
        </Route>
      </Route>

      <Route path={INVITATION_CHECK} element={<InvitationCheckPage />} />
      <Route path={DELETED_PROFILE_PATH} element={<DeletedProfile />} />

      <Route element={<SLRProtectedRoute condition={!isAuthenticated} />}>
        <Route path={ONBOARDING_PATH} element={<DraftPage />} />
      </Route>

      <Route
        element={
          <SLRProtectedRoute
            condition={isAuthenticated}
            callback={handleAuthentication}
            callbackSpinner={<SLRSpinner />}
          />
        }
      >
        <Route
          path={COMPLETE_REGISTRATION_PATH}
          element={<CompleteRegistrationPage />}
        />
      </Route>

      <Route
        path="*"
        element={
          <LegalRoutingWrapper
            legals={legalRouts}
            redirect={PROFILE_PATH}
          />
        }
      />
    </Routes>
  );
};

const BusinessAccountRoute: FC<{ children: React.ReactElement }> = ({
  children
}) => {
  const { data: currentUserProfile, isPending } =
    useGetCurrentUserProfileWithSolution1();

  if (isPending) return <SLRSpinner />;

  const isBusinessAccount =
    currentUserProfile?.billingAddress?.addressType === 'business';
  return isBusinessAccount ? children : <Navigate to={PROFILE_PATH} replace />;
};
